import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Eye, PlusCircle, FileDown } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import { supabase } from "../../Services/supabaseClient";
import {
  getPayrollRuns,
  generatePaymentBatch,
  getPaymentBatches,
  getPaymentBatchLines,
} from "../payrollService";
import { exportPaymentBatchPDF } from "../components/PaymentBatchPDF";

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

const initialForm = {
  payroll_run_id: "",
  batch_name: "",
  payment_date: "",
  payment_method: "bank_transfer",
};

export default function PayrollPayments() {
  const { companyId, company } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");
  const [runs, setRuns] = useState([]);
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [creating, setCreating] = useState(false);

  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchLines, setBatchLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [exportingBatchId, setExportingBatchId] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const [runsData, batchesData] = await Promise.all([
        getPayrollRuns(companyId),
        getPaymentBatches(companyId),
      ]);

      setRuns(runsData || []);
      setBatches(batchesData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load payment data");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const approvedRuns = useMemo(
    () => runs.filter((x) => x.status === "approved"),
    [runs]
  );

  async function handleCreateBatch(e) {
    e.preventDefault();

    try {
      setCreating(true);
      setMsg("");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      await generatePaymentBatch({
        company_id: companyId,
        payroll_run_id: form.payroll_run_id,
        batch_name: form.batch_name,
        payment_date: form.payment_date,
        payment_method: form.payment_method,
        created_by: user?.id || null,
      });

      setForm(initialForm);
      await load();
      setMsg("Payment batch generated successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to generate payment batch");
    } finally {
      setCreating(false);
    }
  }

  async function getEmployeeBankMap() {
    const { data, error } = await supabase
      .from("payroll_employees")
      .select("id, bank_name, bank_account_name, bank_account_number")
      .eq("company_id", companyId);

    if (error) throw error;

    return new Map(
      (data || []).map((row) => [
        row.id,
        {
          bank_name: row.bank_name || "",
          bank_account_name: row.bank_account_name || "",
          bank_account_number: row.bank_account_number || "",
        },
      ])
    );
  }

  async function getEnrichedBatchLines(batchId) {
    const [lines, employeeBankMap] = await Promise.all([
      getPaymentBatchLines(batchId),
      getEmployeeBankMap(),
    ]);

    return (lines || []).map((line) => {
      const employeeBank = employeeBankMap.get(line.employee_id) || {};

      return {
        ...line,
        bank_name: line.bank_name || employeeBank.bank_name || "",
        bank_account_name:
          line.bank_account_name || employeeBank.bank_account_name || "",
        bank_account_number:
          line.bank_account_number || employeeBank.bank_account_number || "",
      };
    });
  }

  async function openBatch(batch) {
    try {
      setSelectedBatch(batch);
      setLoadingLines(true);
      setMsg("");

      const mergedLines = await getEnrichedBatchLines(batch.id);
      setBatchLines(mergedLines);
    } catch (err) {
      setMsg(err.message || "Failed to load payment batch lines");
    } finally {
      setLoadingLines(false);
    }
  }

  async function handleExportBatchPDF(batch) {
    try {
      setExportingBatchId(batch.id);
      setMsg("");

      const lines = await getEnrichedBatchLines(batch.id);
      const run = pickOne(batch.payroll_runs);

      exportPaymentBatchPDF({
        company,
        batch,
        lines,
        payrollRunName: run?.run_name || "",
      });

      setMsg("Payment batch exported successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to export payment batch PDF");
    } finally {
      setExportingBatchId("");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Approved Runs</p>
          <h3>{approvedRuns.length}</h3>
          <span>Available for payment</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Payment Batches</p>
          <h3>{batches.length}</h3>
          <span>Generated payment batches</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Generated Batches</p>
          <h3>{batches.filter((x) => x.status === "generated").length}</h3>
          <span>Ready for transfer/export</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Paid Batches</p>
          <h3>{batches.filter((x) => x.status === "paid").length}</h3>
          <span>Completed payment cycles</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Generate Payment Batch</h3>
            <p>Create payment instructions from approved payroll runs.</p>
          </div>
        </div>

        <form className="payroll-form" onSubmit={handleCreateBatch}>
          <div className="payroll-grid payroll-grid-4">
            <label>
              Approved Payroll Run
              <select
                value={form.payroll_run_id}
                onChange={(e) =>
                  setForm((p) => ({ ...p, payroll_run_id: e.target.value }))
                }
                required
              >
                <option value="">Select payroll run</option>
                {approvedRuns.map((run) => {
                  const period = pickOne(run.payroll_periods);
                  return (
                    <option key={run.id} value={run.id}>
                      {run.run_name} {period ? `- ${period.period_name}` : ""}
                    </option>
                  );
                })}
              </select>
            </label>

            <label>
              Batch Name
              <input
                value={form.batch_name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, batch_name: e.target.value }))
                }
                placeholder="e.g. April 2026 Salary Batch"
              />
            </label>

            <label>
              Payment Date
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) =>
                  setForm((p) => ({ ...p, payment_date: e.target.value }))
                }
                required
              />
            </label>

            <label>
              Payment Method
              <select
                value={form.payment_method}
                onChange={(e) =>
                  setForm((p) => ({ ...p, payment_method: e.target.value }))
                }
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="manual">Manual</option>
                <option value="wallet">Wallet</option>
              </select>
            </label>
          </div>

          <div className="payroll-form-actions">
            <button type="submit" className="primary-btn" disabled={creating}>
              <PlusCircle size={16} />
              {creating ? "Generating..." : "Generate Batch"}
            </button>
          </div>
        </form>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Payment Batches</h3>
            <p>
              View generated payment batches, inspect payment lines, and export
              to PDF.
            </p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Batch Name</th>
                <th>Payroll Run</th>
                <th>Payment Date</th>
                <th>Method</th>
                <th>Status</th>
                <th>Total Amount</th>
                <th>View</th>
                <th>Export</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((row) => {
                const run = pickOne(row.payroll_runs);

                return (
                  <tr key={row.id}>
                    <td>{row.batch_name}</td>
                    <td>{run?.run_name || "-"}</td>
                    <td>{row.payment_date}</td>
                    <td>{row.payment_method}</td>
                    <td>
                      <span className={`pill ${row.status}`}>{row.status}</span>
                    </td>
                    <td>{Number(row.total_amount || 0).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => openBatch(row)}
                        title="View lines"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => handleExportBatchPDF(row)}
                        title="Export PDF"
                        disabled={exportingBatchId === row.id}
                      >
                        <FileDown size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {!batches.length && !busy && (
                <tr>
                  <td colSpan="8">No payment batches found.</td>
                </tr>
              )}

              {busy && (
                <tr>
                  <td colSpan="8">Loading...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedBatch ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Payment Batch Lines — {selectedBatch.batch_name}</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setSelectedBatch(null);
                  setBatchLines([]);
                }}
              >
                Close
              </button>
            </div>

            <div className="payroll-form-actions" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => handleExportBatchPDF(selectedBatch)}
                disabled={exportingBatchId === selectedBatch.id}
              >
                <FileDown size={16} />
                {exportingBatchId === selectedBatch.id
                  ? "Exporting..."
                  : "Export Batch PDF"}
              </button>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Bank</th>
                    <th>Account Name</th>
                    <th>Account Number</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {batchLines.map((line) => (
                    <tr key={line.id}>
                      <td>{line.employee_name}</td>
                      <td>{line.bank_name || "-"}</td>
                      <td>{line.bank_account_name || "-"}</td>
                      <td>{line.bank_account_number || "-"}</td>
                      <td>{Number(line.amount || 0).toLocaleString()}</td>
                      <td>
                        <span className={`pill ${line.status}`}>
                          {line.status}
                        </span>
                      </td>
                    </tr>
                  ))}

                  {!batchLines.length && !loadingLines && (
                    <tr>
                      <td colSpan="6">No payment lines found.</td>
                    </tr>
                  )}

                  {loadingLines && (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
